<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref } from "vue";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef
} from "firebase/storage";
import Preview from "../../../base-components/Preview";
import { useMesasStore } from "../../../services/mesas";
import { useSectoresStore } from "../../../services/sectores";
import { useProductosCategoriasStore } from "../../../services/productosCategorias";
import { useProductosStore } from "../../../services/productos";
import { useTasasIVAStore } from "../../../services/tasas";
import { usePedidosStore } from "../../../services/pedidos";
import { useMensajesStore } from "../../../services/mensajes";
import { numeroFormateado, printHTMLContent } from "../../../utils/helper";
import { userUserStore } from "../../../stores/user";
import {
  mesaConverter,
  type Mesa,
  type Pedido,
  type PedidoItem,
  type Producto
} from "@fixeduy/restaurantestypes";
import {
  crearFacturaFixedVenta,
  type ApiFixedClienteVenta,
  type ApiFixedVentaRequest
} from "../../../services/cloudFunctions/fixed/apiFixedVenta";
import { crearClienteEnFirestore } from "../../../services/clientes";
import ModalGestionPedidosProducto from "./modals/ModalGestionPedidosProducto.vue";
import ModalGestionPedidosProductoManual, {
  type AgregarProductoManualPayload
} from "./modals/ModalGestionPedidosProductoManual.vue";
import ModalGestionPedidosVerPedido from "./modals/ModalGestionPedidosVerPedido.vue";
import FormLabel from "../../../base-components/Form/FormLabel.vue";
import FormTextarea from "../../../base-components/Form/FormTextarea.vue";
import FormInput from "../../../base-components/Form/FormInput.vue";
import { FormSwitch } from "../../../base-components/Form";
import { utilFechaConvertirNumeroString } from "../../../utils/fechaFixedTypes";
import { utilFechaGetCurrentTime } from "../../../utils/fecha";
import { imprimirComanda } from "../../../utils/imprimirComanda";
import { validarCedula } from "../../../utils/validacionCedula";
import { validarRut } from "../../../utils/validarRut";
import { imprimirComandaCompleta } from "../../../utils/imprimirComandaCompleta";
import { useCollection } from "vuefire";
import { collection, getFirestore, query, where } from "firebase/firestore";

const mesasStore = useMesasStore();
const sectoresStore = useSectoresStore();
const productosCategoriasStore = useProductosCategoriasStore();
const productosStore = useProductosStore();
const tasasStore = useTasasIVAStore();
const pedidosStore = usePedidosStore();
const userStore = userUserStore();
const mensajesStore = useMensajesStore();

const vista = ref<"mesas" | "categorias" | "cierre">("mesas");
const mesaSeleccionada = ref<Mesa | null>(null);
const buscarCategoria = ref("");
const categoriaSeleccionada = ref<{ id: string; nombre: string } | null>(null);
const imagenesProductos = ref<Record<string, string>>({});
const productoSeleccionado = ref<Producto | null>(null);
const mostrarModalProducto = ref(false);
const mostrarModalProductoManual = ref(false);
const pedidoEnCurso = ref<{
  pedido: Pedido;
  items: PedidoItem[];
} | null>(null);
const mostrarModalVerPedido = ref(false);
const descuentoMontoFijo = ref<number | null>(null);
const descuentoSeleccionadoIdx = ref<number | null>(null);
const facturaConFixed = ref(false);
const clienteTab = ref<"cf" | "cedula" | "rut" | null>(null);
const clienteCedula = ref("");
const clienteRUT = ref("");
const clienteRazonSocial = ref("");
const clienteNombreComercial = ref("");
const clienteDomicilio = ref("");
const clienteLocalidad = ref("");
const clienteDepartamento = ref("");
const clienteDomicilioEntrega = ref("");
const clienteMailEnvioFactura = ref("");
const clienteTelefono = ref("");
const clienteObservaciones = ref("");

// Loading por acción para evitar dobles clics
const cargandoMesaId = ref<string | null>(null);
const cargandoCategoriaId = ref<string | null>(null);
const cargandoProductoId = ref<string | null>(null);
const cargandoAgregarProducto = ref(false);
const cargandoAgregarProductoManual = ref(false);
const cargandoEnviarComanda = ref(false);
const cargandoCerrarMesa = ref(false);

onBeforeMount(async () => {
  if (!mesasStore.mesas.length && !mesasStore.cargando) {
    await mesasStore.getFromFirestore();
  }
  if (!sectoresStore.sectores.length && !sectoresStore.cargando) {
    await sectoresStore.getFromFirestore();
  }
  await productosCategoriasStore.getCategoriasFromFirestore();
  if (!tasasStore.tasas.length) {
    await tasasStore.getTasasFromFirestore();
  }
  if (!productosStore.productos.length && !productosStore.cargando) {
    await productosStore.getFromFirestore();
  }
  await cargarImagenesProductos();
});

const db = getFirestore();
const mesasActivas = useCollection(
  query(
    collection(db, `empresas/${userStore.empresa?.id}/mesas`),
    where("activo", "==", true)
  ).withConverter(mesaConverter)
);

const mesasActivasOrdenadas = computed(() =>
  [...mesasActivas.value].sort((a, b) => a.orden - b.orden)
);

const categoriasVisibles = computed(() => {
  const texto = buscarCategoria.value.toLowerCase();
  return productosCategoriasStore.categorias
    .filter(c => {
      if (!c.activo) return false;
      if (!texto) return true;
      return c.nombre.toLowerCase().includes(texto);
    })
    .sort((a, b) =>
      a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" })
    );
});

const hayTextoBusqueda = computed(
  () => buscarCategoria.value.trim().length > 0
);

const productosBuscados = computed<Producto[]>(() => {
  if (!hayTextoBusqueda.value) return [];
  const texto = buscarCategoria.value.toLowerCase().trim();
  return productosStore.productos.filter(p => {
    if (!p.activo) return false;
    const nombreProducto = p.nombre?.toLowerCase() ?? "";
    const nombreCategoria =
      p.categoriaNombre?.toLowerCase() ??
      productosCategoriasStore.categorias
        .find(c => c.id === p.categoriaId)
        ?.nombre.toLowerCase() ??
      "";
    return nombreProducto.includes(texto) || nombreCategoria.includes(texto);
  });
});

const productosVisibles = computed<Producto[]>(() => {
  if (!categoriaSeleccionada.value) return [];
  const texto = buscarCategoria.value.toLowerCase();
  return productosStore.productos.filter(p => {
    if (!p.activo) return false;
    if (p.categoriaId !== categoriaSeleccionada.value!.id) return false;
    if (!texto) return true;
    return p.nombre.toLowerCase().includes(texto);
  });
});

const itemsPedidoEnCurso = computed(() => pedidoEnCurso.value?.items ?? []);
const itemsPendientesEnPedido = computed(() =>
  itemsPedidoEnCurso.value.filter(
    i => (i.estado ?? "").toLowerCase() === "pendiente"
  )
);
const hayItemsPendientes = computed(
  () => itemsPendientesEnPedido.value.length > 0
);

const totalesPedidoEnCurso = computed(() => ({
  subtotal: itemsPedidoEnCurso.value.reduce(
    (acc, item) => acc + item.subtotal,
    0
  ),
  iva: itemsPedidoEnCurso.value.reduce((acc, item) => acc + item.montoIva, 0),
  total: itemsPedidoEnCurso.value.reduce((acc, item) => acc + item.total, 0)
}));

const cargarImagenesProductos = async () => {
  const storage = getStorage();

  await Promise.all(
    productosStore.productos.map(async producto => {
      if (imagenesProductos.value[producto.id]) return;

      if (producto.fotoUrl) {
        imagenesProductos.value[producto.id] = producto.fotoUrl;
        return;
      }

      if (!producto.fotoStoragePath) return;

      try {
        const url = await getDownloadURL(
          storageRef(storage, producto.fotoStoragePath)
        );
        imagenesProductos.value[producto.id] = url;
      } catch (error) {
        console.error(
          `No se pudo cargar la imagen del producto ${producto.id}:`,
          error
        );
      }
    })
  );
};

const obtenerClasesEstado = (estado?: string | null) => {
  const e = (estado || "").toLowerCase();

  if (e.includes("ocup")) {
    return {
      card: "bg-rose-50 border-rose-200",
      badge: "bg-rose-100 text-rose-700"
    };
  }
  if (e.includes("reserv")) {
    return {
      card: "bg-amber-50 border-amber-200",
      badge: "bg-amber-100 text-amber-700"
    };
  }
  return {
    card: "bg-emerald-50 border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700"
  };
};

const esMesaDisponible = (estado?: string | null) => {
  const e = (estado || "").toLowerCase();
  return e === "" || e === "disponible";
};

const obtenerNombreSector = (sectorId?: string | null) => {
  if (!sectorId) return "Sin sector";
  return (
    sectoresStore.sectores.find(sector => sector.id === sectorId)?.nombre ??
    sectorId
  );
};

const getPedidoActivoPorMesa = async (mesaId: string) => {
  const pedidos = await pedidosStore.getPedidosPorMesaId(mesaId);

  // Consideramos "activos" los que no estén cerrados/cancelados.
  const activos = pedidos
    .filter(p => p.activo !== false)
    .filter(p => p.estado !== "cerrado" && p.estado !== "cancelado")
    .sort((a, b) => (b.fechaActualizacion ?? 0) - (a.fechaActualizacion ?? 0));

  if (activos.length === 0) return null;

  // Garantizamos unicidad: si hay más de uno activo, dejamos el más reciente.
  const elegido = activos[0];
  const otros = activos.slice(1);
  for (const p of otros) {
    await pedidosStore.modificarEstado(p.id, "cancelado").catch(() => {});
  }

  return elegido;
};

const abrirMesa = async (mesa: Mesa) => {
  if (cargandoMesaId.value && cargandoMesaId.value !== mesa.id) return;
  cargandoMesaId.value = mesa.id;

  try {
    // Un frame para que el spinner se vea antes de cambiar la vista.
    await nextTick();
    mesaSeleccionada.value = mesa;

    if (esMesaDisponible(mesa.estado)) {
      pedidoEnCurso.value = null;
      categoriaSeleccionada.value = null;
      buscarCategoria.value = "";
      vista.value = "categorias";
      return;
    }

    const pedidoActivo = await getPedidoActivoPorMesa(mesa.id);
    if (!pedidoActivo) {
      // fallback: no hay pedido activo, tratamos como mesa disponible a nivel UI
      pedidoEnCurso.value = null;
      categoriaSeleccionada.value = null;
      buscarCategoria.value = "";
      vista.value = "categorias";
      return;
    }

    const items = await pedidosStore.getPedidoById(pedidoActivo.id);
    pedidoEnCurso.value = {
      pedido: pedidoActivo,
      items
    };

    categoriaSeleccionada.value = null;
    buscarCategoria.value = "";
    vista.value = "categorias";
  } finally {
    cargandoMesaId.value = null;
  }
};

const abrirCategoria = async (categoria: { id: string; nombre: string }) => {
  if (cargandoCategoriaId.value && cargandoCategoriaId.value !== categoria.id)
    return;
  cargandoCategoriaId.value = categoria.id;

  try {
    // Un frame para que el spinner se vea antes de cambiar de vista.
    await nextTick();
    categoriaSeleccionada.value = {
      id: categoria.id,
      nombre: categoria.nombre
    };
    buscarCategoria.value = "";
  } finally {
    cargandoCategoriaId.value = null;
  }
};

const abrirProducto = async (producto: Producto) => {
  if (cargandoProductoId.value && cargandoProductoId.value !== producto.id)
    return;
  cargandoProductoId.value = producto.id;

  try {
    // Un frame para que el spinner se vea antes de abrir el modal.
    await nextTick();
    productoSeleccionado.value = producto;
    mostrarModalProducto.value = true;
  } finally {
    cargandoProductoId.value = null;
  }
};

const abrirModalProductoManual = () => {
  mostrarModalProductoManual.value = true;
};

const cerrarModalProductoManual = () => {
  mostrarModalProductoManual.value = false;
};

const cerrarModalProducto = () => {
  mostrarModalProducto.value = false;
  productoSeleccionado.value = null;
};

const productoSrc = (producto: Producto) =>
  imagenesProductos.value[producto.id] ??
  producto.fotoUrl ??
  "/productoSinImagen.png";

const crearPedidoEnCurso = async () => {
  if (!mesaSeleccionada.value) return null;

  descuentoMontoFijo.value = null;
  descuentoSeleccionadoIdx.value = null;

  const mesa = mesaSeleccionada.value;

  // Si ya hay un pedido activo en DB para esa mesa, continuamos ese en vez de crear otro.
  const pedidoActivoExistente = await getPedidoActivoPorMesa(mesa.id);
  if (pedidoActivoExistente) {
    const items = await pedidosStore.getPedidoById(pedidoActivoExistente.id);
    pedidoEnCurso.value = {
      pedido: pedidoActivoExistente,
      items
    };

    // Sincronizamos la UI de descuento con los valores guardados en el pedido.
    const descuentoMonto = Number(pedidoActivoExistente.descuentoMonto ?? 0);
    const descuentoPorcentaje = Number(
      pedidoActivoExistente.descuentoPorcentaje ?? 0
    );
    if (descuentoMonto > 0) {
      descuentoMontoFijo.value = descuentoMonto;
      descuentoSeleccionadoIdx.value = null;
    } else if (descuentoPorcentaje > 0) {
      descuentoMontoFijo.value = null;
      const idx = descuentosActivos.value.findIndex(
        d => Number(d.valor ?? 0) === descuentoPorcentaje
      );
      descuentoSeleccionadoIdx.value = idx >= 0 ? idx : null;
    }

    await mesasStore.updateEstado(mesa.id, "ocupada");
    mesaSeleccionada.value = { ...mesa, estado: "ocupada" };
    return pedidoEnCurso.value;
  }

  const datos: Pedido = {
    version: 1,
    id: "",
    numero: 0,
    estado: "borrador",
    origen: "salon",
    mesaId: mesa.id,
    mesaNombre: mesa.nombre,
    mesaCapacidad: mesa.capacidad,
    sectorId: mesa.sectorId ?? "",
    mozoId: null,
    cliente: null,
    incluyeIva: true,
    subtotal: 0,
    montoIva: 0,
    total: 0,
    moneda: "pesos",
    cotizacion: 1,
    local: "",
    observaciones: "",
    fechaCreacion: 0,
    fechaActualizacion: 0,
    fechaCierre: 0,
    activo: true,
    tipoDescuento: "",
    descuentoMonto: 0,
    descuentoPorcentaje: 0,
    usuario: userStore.user?.uid!,
    creadaDesde: "web",
    cfeId: "",
    cfeNumero: 0,
    cfeSerie: "",
    cfeTipo: "",
    ventaId: "",
    facturado: false,
    devuelto: false,
    cfeIdDevolucion: "",
    cfeNumeroDevolucion: 0,
    cfeSerieDevolucion: "",
    cfeTipoDevolucion: "",
    ventaIdDevolucion: "",
    procesadoEstadisticas: false
  };

  const pedido = await pedidosStore.crearPedido(datos);

  // Marcar mesa como ocupada en la base (y actualizar store local).
  await mesasStore.updateEstado(mesa.id, "ocupada");
  mesaSeleccionada.value = { ...mesa, estado: "ocupada" };

  pedidoEnCurso.value = {
    pedido,
    items: []
  };

  return pedidoEnCurso.value;
};

const recalcularTotalesPedido = () => {
  if (!pedidoEnCurso.value) return;

  pedidoEnCurso.value.pedido.subtotal = totalesPedidoEnCurso.value.subtotal;
  pedidoEnCurso.value.pedido.montoIva = totalesPedidoEnCurso.value.iva;
  pedidoEnCurso.value.pedido.total = totalesPedidoEnCurso.value.total;
  pedidoEnCurso.value.pedido.fechaActualizacion = Date.now();
};

const agregarProductoAlPedido = async (payload: {
  cantidad: number;
  observaciones: string;
}) => {
  if (!productoSeleccionado.value || !mesaSeleccionada.value) return;
  if (cargandoAgregarProducto.value) return;
  cargandoAgregarProducto.value = true;

  try {
    let pedidoActual = pedidoEnCurso.value;
    if (!pedidoActual) {
      pedidoActual = await crearPedidoEnCurso();
    }
    if (!pedidoActual) return;

    const producto = productoSeleccionado.value;
    const tasa = tasasStore.getTasaIVAById(producto.tasaIvaId ?? "");
    const cantidad = payload.cantidad;
    const precioUnitarioConIva = producto.precioConIva;
    const precioUnitarioSinIva = producto.precioSinIva;
    const subtotal = Number((precioUnitarioSinIva * cantidad).toFixed(2));
    const total = Number((precioUnitarioConIva * cantidad).toFixed(2));
    const montoIva = Number((total - subtotal).toFixed(2));
    const ahora = Date.now();

    const itemBase: PedidoItem = {
      id: "",
      pedidoId: "",
      version: 1,
      tipo: "producto",
      productoId: producto.id,
      nombre: producto.nombre,
      precioUnitarioConIva,
      precioUnitarioSinIva,
      tasaIvaId: tasa?.id!,
      tasaIvaValor: tasa?.valor ?? 0,
      cantidad,
      montoIva,
      subtotal,
      total,
      observaciones: payload.observaciones,
      fechaCreacion: ahora,
      activo: true,
      estado: "pendiente",
      rubro:
        producto.categoriaNombre ?? categoriaSeleccionada.value?.nombre ?? "",
      rubroId: producto.categoriaId ?? categoriaSeleccionada.value?.id ?? "",
      descuentoMonto: 0,
      codigoProducto: "",
      impresionSectores: producto.impresionSectores
    };

    const itemCreado = await pedidosStore.crearPedidoItem(
      pedidoActual.pedido.id,
      itemBase
    );

    pedidoActual.items.push(itemCreado);
    recalcularTotalesPedido();

    const estadoPedido = pedidoActual.pedido.estado;
    await pedidosStore.updateTotalesPedido(pedidoActual.pedido.id, {
      subtotal: pedidoActual.pedido.subtotal,
      montoIva: pedidoActual.pedido.montoIva,
      total: pedidoActual.pedido.total,
      estado: estadoPedido
    });
    cerrarModalProducto();

    // Volver a la pantalla de categorías luego de agregar
    categoriaSeleccionada.value = null;
    buscarCategoria.value = "";
  } finally {
    cargandoAgregarProducto.value = false;
  }
};

const agregarProductoManualAlPedido = async (
  payload: AgregarProductoManualPayload
) => {
  if (!mesaSeleccionada.value) return;
  if (cargandoAgregarProductoManual.value) return;
  cargandoAgregarProductoManual.value = true;

  try {
    let pedidoActual = pedidoEnCurso.value;
    if (!pedidoActual) {
      pedidoActual = await crearPedidoEnCurso();
    }
    if (!pedidoActual) return;

    const cantidad = payload.cantidad;
    const precioUnitarioConIva = payload.precioUnitarioConIva;
    const precioUnitarioSinIva = payload.precioUnitarioSinIva;
    const subtotal = Number((precioUnitarioSinIva * cantidad).toFixed(2));
    const total = Number((precioUnitarioConIva * cantidad).toFixed(2));
    const montoIva = Number((total - subtotal).toFixed(2));
    const ahora = Date.now();

    const itemBase: PedidoItem = {
      id: "",
      pedidoId: "",
      version: 1,
      tipo: "manual",
      productoId: "",
      nombre: payload.nombre,
      precioUnitarioConIva,
      precioUnitarioSinIva,
      tasaIvaId: payload.tasaIvaId,
      tasaIvaValor: payload.tasaValor,
      cantidad,
      montoIva,
      subtotal,
      total,
      observaciones: payload.observaciones,
      fechaCreacion: ahora,
      activo: true,
      estado: "pendiente",
      rubro: payload.categoriaNombre,
      rubroId: payload.categoriaId,
      descuentoMonto: 0,
      codigoProducto: "",
      impresionSectores: payload.impresionSectores
    };

    const itemCreado = await pedidosStore.crearPedidoItem(
      pedidoActual.pedido.id,
      itemBase
    );

    pedidoActual.items.push(itemCreado);
    recalcularTotalesPedido();

    const estadoPedido = pedidoActual.pedido.estado;
    await pedidosStore.updateTotalesPedido(pedidoActual.pedido.id, {
      subtotal: pedidoActual.pedido.subtotal,
      montoIva: pedidoActual.pedido.montoIva,
      total: pedidoActual.pedido.total,
      estado: estadoPedido
    });
    cerrarModalProductoManual();
  } finally {
    cargandoAgregarProductoManual.value = false;
  }
};

const volverACategorias = () => {
  categoriaSeleccionada.value = null;
  buscarCategoria.value = "";
};

const limpiarFormularioCliente = () => {
  clienteTab.value = null;
  clienteCedula.value = "";
  clienteRUT.value = "";
  clienteRazonSocial.value = "";
  clienteNombreComercial.value = "";
  clienteDomicilio.value = "";
  clienteLocalidad.value = "";
  clienteDepartamento.value = "";
  clienteDomicilioEntrega.value = "";
  clienteMailEnvioFactura.value = "";
  clienteTelefono.value = "";
  clienteObservaciones.value = "";
};

const construirClienteParaFixed = (): ApiFixedClienteVenta | null => {
  if (clienteTab.value === "rut") {
    return {
      tipoDocumento: 2,
      documento: clienteRUT.value.trim(),
      razonSocial: clienteRazonSocial.value.trim(),
      nombreComercial: clienteNombreComercial.value.trim(),
      domicilio: clienteDomicilio.value.trim(),
      localidad: clienteLocalidad.value.trim(),
      departamento: clienteDepartamento.value.trim(),
      domicilioEntrega: clienteDomicilioEntrega.value.trim(),
      mailEnvioFactura: clienteMailEnvioFactura.value.trim(),
      telefono: clienteTelefono.value.trim(),
      observaciones: clienteObservaciones.value.trim()
    };
  }
  if (clienteTab.value === "cedula") {
    return {
      tipoDocumento: 3,
      documento: clienteCedula.value.trim(),
      razonSocial: clienteRazonSocial.value.trim(),
      nombreComercial: clienteNombreComercial.value.trim(),
      domicilio: clienteDomicilio.value.trim(),
      localidad: clienteLocalidad.value.trim(),
      departamento: clienteDepartamento.value.trim(),
      domicilioEntrega: clienteDomicilioEntrega.value.trim(),
      mailEnvioFactura: clienteMailEnvioFactura.value.trim(),
      telefono: clienteTelefono.value.trim(),
      observaciones: clienteObservaciones.value.trim()
    };
  }
  return null;
};

const limpiarPedidoEnCurso = () => {
  pedidoEnCurso.value = null;
  mostrarModalVerPedido.value = false;
  limpiarFormularioCliente();
};

const volverAMesas = () => {
  vista.value = "mesas";
  mesaSeleccionada.value = null;
  categoriaSeleccionada.value = null;
  buscarCategoria.value = "";
  pedidoEnCurso.value = null;
  cerrarModalProducto();
  cerrarModalProductoManual();
  mostrarModalVerPedido.value = false;
  limpiarFormularioCliente();
};

const actualizarItemsPedidoEnCurso = (items: PedidoItem[]) => {
  if (!pedidoEnCurso.value) return;
  pedidoEnCurso.value.items = items;

  // recalculo totales del pedido
  const subtotal = items.reduce((acc, item) => acc + (item.subtotal ?? 0), 0);
  const iva = items.reduce((acc, item) => acc + (item.montoIva ?? 0), 0);
  const total = items.reduce((acc, item) => acc + (item.total ?? 0), 0);

  pedidoEnCurso.value.pedido.subtotal = subtotal;
  pedidoEnCurso.value.pedido.montoIva = iva;
  pedidoEnCurso.value.pedido.total = total;
  pedidoEnCurso.value.pedido.fechaActualizacion = Date.now();
};

const construirPayloadFixedVenta = (
  _pedido: Pedido,
  items: PedidoItem[]
): ApiFixedVentaRequest => {
  const cli = construirClienteParaFixed();
  return {
    apiKey: "JU4L6W22-0P8A-G5F3-PN3Z-438UNRWV9G5J",
    rutComercio: userStore.empresa?.rut.toString()!,
    cliente: cli ?? {},
    fecha:
      utilFechaConvertirNumeroString(
        pedidoEnCurso.value?.pedido.fechaCierre!,
        false,
        "/"
      ) ??
      utilFechaConvertirNumeroString(utilFechaGetCurrentTime(), false, "/"),
    moneda: "UYU",
    montoBruto: 1,
    lineasVenta: items.map((it, idx) => ({
      cantidad: it.cantidad,
      descripcion: it.nombre ?? "",
      descripcionLarga: "",
      rubro: it.rubro,
      tasaIva: tasasStore.getTasaIVAById(it.tasaIvaId)?.cfeDgi!,
      descuentoMonto: it.descuentoMonto ?? 0,
      precioUnitario: it.precioUnitarioConIva ?? 0
    })),
    observaciones: `# ${pedidoEnCurso.value?.pedido.numero} | ${pedidoEnCurso.value?.pedido.mesaNombre} | ${utilFechaConvertirNumeroString(pedidoEnCurso.value?.pedido.fechaCreacion!, true, "/")} - ${utilFechaConvertirNumeroString(utilFechaGetCurrentTime(), true, "/")}`,
    creadaDesde: "web"
  };
};

const enviarPedidoEnCurso = async () => {
  if (!pedidoEnCurso.value) return;
  if (cargandoEnviarComanda.value) return;
  cargandoEnviarComanda.value = true;

  try {
    const id = pedidoEnCurso.value.pedido.id;

    const pendingCount = hayItemsPendientes.value
      ? itemsPendientesEnPedido.value.length
      : 0;

    // “Enviar” = pasar de borrador a abierto y enviar solo ítems pendientes
    const ok = await pedidosStore.updatePedido(id, {
      estado: "abierto",
      subtotal: pedidoEnCurso.value.pedido.subtotal,
      montoIva: pedidoEnCurso.value.pedido.montoIva,
      total: pedidoEnCurso.value.pedido.total
    });

    for (const sector of userStore.opcionesGenerales.sectores) {
      const ticketera = userStore.opcionesGeneralesTicketera.ticketeras.find(
        t => t.idSector === sector
      );

      if (!ticketera) {
        console.warn(`No se encontró ticketera para el sector ${sector}`);
        continue;
      }

      await imprimirComanda({
        pedido: pedidoEnCurso.value.pedido,
        lineas: pedidoEnCurso.value.items,
        sector: sector,
        ip: ticketera.direccion,
        port: ticketera.puerto
      });
    }
    if (!ok) {
      mensajesStore.mostrarModal(
        "Error al enviar a comanda",
        "No se pudo actualizar el pedido. Intentá nuevamente.",
        mensajesStore.mensajeType.error
      );
      return;
    }

    pedidoEnCurso.value.pedido.estado = "abierto";

    // Cambiar estado de ítems pendientes a 'enviada'
    if (pendingCount > 0) {
      const enviados = await pedidosStore.enviarPendientesPedidoItems(
        id,
        "enviada"
      );
      // sincronizar UI local
      pedidoEnCurso.value.items = pedidoEnCurso.value.items.map(it => {
        if ((it.estado ?? "").toLowerCase() === "pendiente") {
          return { ...it, estado: "enviada" };
        }
        return it;
      });
      // Si no hubo enviados por cualquier motivo, no mostramos éxito
      if (enviados === 0) {
        return;
      }
    }

    // asegurar que la mesa quede marcada como ocupada
    if (mesaSeleccionada.value?.id) {
      await mesasStore.updateEstado(mesaSeleccionada.value.id, "ocupada");
      mesaSeleccionada.value = {
        ...mesaSeleccionada.value,
        estado: "ocupada"
      } as Mesa;
    }

    mensajesStore.mostrarToast(
      "Enviado a comanda con éxito",
      "",
      mensajesStore.mensajeType.exito
    );
  } finally {
    cargandoEnviarComanda.value = false;
  }
};

const cerrarMesa = async () => {
  if (!pedidoEnCurso.value || !mesaSeleccionada.value) return;
  if (cargandoCerrarMesa.value) return;

  if (clienteTab.value === "cedula") {
    const doc = clienteCedula.value.trim();
    if (!doc || !validarCedula(doc)) {
      mensajesStore.mostrarModal(
        "Cédula inválida",
        "El número de cédula no es válido. Verificá el documento.",
        mensajesStore.mensajeType.error
      );
      return;
    }
  }
  if (clienteTab.value === "rut") {
    const doc = clienteRUT.value.trim();
    if (!doc || !validarRut(doc)) {
      mensajesStore.mostrarModal(
        "RUT inválido",
        "El RUT ingresado no es válido. Verificá el documento.",
        mensajesStore.mensajeType.error
      );
      return;
    }
  }

  cargandoCerrarMesa.value = true;

  try {
    const pedidoId = pedidoEnCurso.value.pedido.id;
    const ahora = Date.now();

    const toCents = (n: number) => Math.round((n ?? 0) * 100);
    const fromCents = (c: number) => c / 100;

    const descuentoPorcentaje = Math.max(
      0,
      descuentoSeleccionadoIdx.value == null
        ? 0
        : Number(
            descuentosActivos.value[descuentoSeleccionadoIdx.value]?.valor ?? 0
          )
    );
    const descuentoMontoInput = Math.max(
      0,
      Number(descuentoMontoFijo.value ?? 0)
    );

    const tipoDescuento =
      descuentoMontoFijo.value != null && descuentoMontoFijo.value > 0
        ? "Monto fijo"
        : descuentoSeleccionadoIdx.value != null && descuentoPorcentaje > 0
          ? descuentosActivos.value[descuentoSeleccionadoIdx.value]?.nombre
          : "";

    const itemsBase = pedidoEnCurso.value.items ?? [];
    const totalBaseCents = itemsBase.reduce((acc, it) => {
      return acc + toCents(it.total ?? 0);
    }, 0);

    // Descuento monetario total aplicado (en cents), nunca puede superar el total.
    const descuentoTotalAplicadoCents =
      tipoDescuento === "Monto fijo"
        ? Math.min(toCents(descuentoMontoInput), totalBaseCents)
        : tipoDescuento != "Monto fijo" && tipoDescuento
          ? Math.min(
              Math.round((totalBaseCents * descuentoPorcentaje) / 100),
              totalBaseCents
            )
          : 0;

    const updatedItems = itemsBase.map(it => ({ ...it }));

    if (descuentoTotalAplicadoCents > 0 && totalBaseCents > 0) {
      // Distribución por prorrateo del total de cada línea.
      const descuentosLineCents: number[] = updatedItems.map(() => 0);
      const restos: Array<{ idx: number; resto: number }> = updatedItems.map(
        (it, idx) => {
          const itemTotalCents = toCents(it.total ?? 0);
          const numerador = itemTotalCents * descuentoTotalAplicadoCents;
          const restoNumerico = numerador % totalBaseCents;
          return { idx, resto: restoNumerico };
        }
      );

      // Pisos por centésimo con ajuste final para que la suma cuadre exacto.
      let sumPisos = 0;
      updatedItems.forEach((it, idx) => {
        const itemTotalCents = toCents(it.total ?? 0);
        const numerador = itemTotalCents * descuentoTotalAplicadoCents;
        const piso = Math.floor(numerador / totalBaseCents);
        descuentosLineCents[idx] = piso;
        sumPisos += piso;
      });

      let remainder = descuentoTotalAplicadoCents - sumPisos;
      if (remainder > 0) {
        restos
          .sort((a, b) => b.resto - a.resto)
          .slice(0, remainder)
          .forEach(({ idx }) => {
            descuentosLineCents[idx] += 1;
          });
      }

      // Aplicar descuento línea por línea.
      updatedItems.forEach((it, idx) => {
        const itemTotalCents = toCents(it.total ?? 0);
        const itemSubtotalCents = toCents(it.subtotal ?? 0);

        const descuentoLineaCents = descuentosLineCents[idx] ?? 0;
        const nuevoTotalCents = Math.max(
          0,
          itemTotalCents - descuentoLineaCents
        );
        const factor =
          itemTotalCents > 0 ? nuevoTotalCents / itemTotalCents : 0;

        // Mantenemos consistencia entre subtotal/IVA y el nuevo total.
        const nuevoSubtotalCents = Math.round(itemSubtotalCents * factor);
        const nuevoMontoIvaCents = nuevoTotalCents - nuevoSubtotalCents;

        it.descuentoMonto = fromCents(descuentoLineaCents);
        it.total = fromCents(nuevoTotalCents);
        it.subtotal = fromCents(nuevoSubtotalCents);
        it.montoIva = fromCents(nuevoMontoIvaCents);
      });
    } else {
      // Sin descuento: dejamos descuentoMonto en 0.
      updatedItems.forEach(it => {
        it.descuentoMonto = 0;
      });
    }

    // Sincronizamos la UI/cálculo local con los valores finales por línea.
    pedidoEnCurso.value.items = updatedItems;

    const pedidoSubtotalCents = updatedItems.reduce((acc, it) => {
      return acc + toCents(it.subtotal ?? 0);
    }, 0);
    const pedidoMontoIvaCents = updatedItems.reduce((acc, it) => {
      return acc + toCents(it.montoIva ?? 0);
    }, 0);
    const pedidoTotalCents = updatedItems.reduce((acc, it) => {
      return acc + toCents(it.total ?? 0);
    }, 0);

    const descuentoMontoAplicado = fromCents(descuentoTotalAplicadoCents);
    const descuentoPorcentajePersistido =
      tipoDescuento != "Monto Fijo" && tipoDescuento ? descuentoPorcentaje : 0;

    // Persistimos ítems solo cuando hubo descuento real.
    if (descuentoTotalAplicadoCents > 0 && totalBaseCents > 0) {
      const okItems = await Promise.all(
        updatedItems.map(it =>
          pedidosStore.updatePedidoItem(pedidoId, it.id, {
            descuentoMonto: it.descuentoMonto ?? 0,
            subtotal: it.subtotal ?? 0,
            montoIva: it.montoIva ?? 0,
            total: it.total ?? 0
          })
        )
      );

      if (!okItems.every(Boolean)) {
        mensajesStore.mostrarModal(
          "Error al cerrar mesa",
          "No se pudo actualizar los ítems del pedido con el descuento. Intentá nuevamente.",
          mensajesStore.mensajeType.error
        );
        return;
      }
    }

    const clienteFactura = construirClienteParaFixed();
    const clientePedidoStr =
      clienteFactura != null ? JSON.stringify(clienteFactura) : null;

    const okPedido = await pedidosStore.updatePedido(pedidoId, {
      estado: "cerrado",
      fechaCierre: ahora,
      subtotal: fromCents(pedidoSubtotalCents),
      montoIva: fromCents(pedidoMontoIvaCents),
      total: fromCents(pedidoTotalCents),
      observaciones: pedidoEnCurso.value.pedido.observaciones ?? "",
      tipoDescuento,
      descuentoMonto: descuentoMontoAplicado,
      descuentoPorcentaje: descuentoPorcentajePersistido,
      ...(clientePedidoStr != null ? { cliente: clientePedidoStr } : {})
    });

    if (!okPedido) {
      mensajesStore.mostrarModal(
        "Error al cerrar mesa",
        "No se pudo actualizar el pedido. Intentá nuevamente.",
        mensajesStore.mensajeType.error
      );
      return;
    }

    if (clienteFactura) {
      void crearClienteEnFirestore(clienteFactura, pedidoId);
    }

    pedidoEnCurso.value.pedido.estado = "cerrado";
    pedidoEnCurso.value.pedido.fechaCierre = ahora;
    pedidoEnCurso.value.pedido.total = fromCents(pedidoTotalCents);
    pedidoEnCurso.value.pedido.subtotal = fromCents(pedidoSubtotalCents);
    pedidoEnCurso.value.pedido.montoIva = fromCents(pedidoMontoIvaCents);
    pedidoEnCurso.value.pedido.tipoDescuento = tipoDescuento;
    pedidoEnCurso.value.pedido.descuentoMonto = descuentoMontoAplicado;
    pedidoEnCurso.value.pedido.descuentoPorcentaje =
      descuentoPorcentajePersistido;

    const okMesa = await mesasStore.updateEstado(
      mesaSeleccionada.value.id,
      "disponible"
    );
    if (!okMesa) {
      mensajesStore.mostrarModal(
        "Error al cerrar mesa",
        "No se pudo actualizar el estado de la mesa. Revisá la configuración.",
        mensajesStore.mensajeType.error
      );
      return;
    }
    mesaSeleccionada.value = {
      ...mesaSeleccionada.value,
      estado: "disponible"
    };

    const payloadFixed: ApiFixedVentaRequest | null =
      facturaConFixed.value && updatedItems.length > 0
        ? construirPayloadFixedVenta(pedidoEnCurso.value.pedido, updatedItems)
        : null;

    if (userStore.opcionesGenerales.imprimirComandaCompleta) {
      const ticketera = userStore.opcionesGeneralesTicketera.ticketeras.find(
        t => t.idSector === userStore.opcionesGenerales.impresionComandaCompleta
      );

      if (ticketera) {
        await imprimirComandaCompleta({
          pedido: pedidoEnCurso.value.pedido,
          lineas: pedidoEnCurso.value.items,
          ip: ticketera.direccion,
          port: ticketera.puerto
        });
      }
    }
    // limpiar flujo
    pedidoEnCurso.value = null;
    mostrarModalVerPedido.value = false;
    categoriaSeleccionada.value = null;
    buscarCategoria.value = "";
    limpiarFormularioCliente();

    // Mensaje de cierre siempre (el de Fixed es adicional)
    mensajesStore.mostrarToast(
      "Mesa cerrada con éxito",
      "",
      mensajesStore.mensajeType.exito
    );
    vista.value = "mesas";

    // Llamada a Fixed en background para no bloquear la UI.
    if (payloadFixed) {
      void (async () => {
        try {
          const respuesta = await crearFacturaFixedVenta(payloadFixed);

          // Si Fixed respondió OK, seteamos y persistimos los datos de la CFE en el pedido.
          // Evitamos guardar `undefined` si Fixed no devolviera alguno de los campos.
          const puedenPersistirCFE =
            respuesta?.idCFE != null &&
            respuesta?.numero != null &&
            respuesta?.serie != null &&
            respuesta?.tipoCFE != null &&
            respuesta?.idVenta != null;

          if (puedenPersistirCFE) {
            const okPersist = await pedidosStore.updatePedido(pedidoId, {
              cfeId: respuesta.idCFE,
              cfeNumero: respuesta.numero,
              cfeSerie: respuesta.serie,
              cfeTipo: respuesta.tipoCFE,
              ventaId: respuesta.idVenta,
              facturado: true,
              devuelto: false
            });

            if (!okPersist) {
              mensajesStore.mostrarToast(
                "Factura Fixed generada",
                "Pero no se pudo guardar en la BD. Intentá nuevamente.",
                mensajesStore.mensajeType.error
              );
            }
          } else {
            mensajesStore.mostrarToast(
              "Factura Fixed generada",
              "Fixed no devolvió datos completos de CFE para guardar en la BD.",
              mensajesStore.mensajeType.error
            );
          }

          mensajesStore.mostrarToast(
            "Factura Fixed enviada",
            "Cargando impresion...",
            mensajesStore.mensajeType.exito
          );
          // await imprimirCFEA4();
          // await imprimirCFETicket();
          await printHTMLContent(respuesta.htmlTicket);
        } catch (e) {
          console.error("Error al generar factura Fixed:", e);
          mensajesStore.mostrarToast(
            "Error al generar factura Fixed",
            "Intentá nuevamente.",
            mensajesStore.mensajeType.error
          );
        }
      })();
    }
  } finally {
    cargandoCerrarMesa.value = false;
  }
};

const irACierreMesa = () => {
  if (!pedidoEnCurso.value) return;
  vista.value = "cierre";
  const og = userStore.opcionesGenerales;
  facturaConFixed.value = !!(og?.facturaFixed && og?.fixedPorDefecto);
};

const volverAPedido = () => {
  vista.value = "categorias";
};

const descuentosActivos = computed(() => {
  const og = userStore.opcionesGenerales;
  return (og.descuentos ?? []).filter(d => d.activo);
});

const descuentoAplicado = computed(() => {
  const total = pedidoEnCurso.value?.pedido.total ?? 0;
  const fijo = Number(descuentoMontoFijo.value ?? 0);
  if (fijo > 0) return Math.min(fijo, total);
  if (descuentoSeleccionadoIdx.value == null) return 0;
  const d = descuentosActivos.value[descuentoSeleccionadoIdx.value];
  if (!d) return 0;
  const pct = Number(d.valor ?? 0);
  if (pct <= 0) return 0;
  return Math.min((total * pct) / 100, total);
});

const descuentoMontoFijoExcedeTotal = computed(() => {
  if (descuentoMontoFijo.value == null) return false;
  const total = Number(pedidoEnCurso.value?.pedido.total ?? 0);
  const fijo = Number(descuentoMontoFijo.value ?? 0);
  if (!Number.isFinite(total) || !Number.isFinite(fijo)) return false;
  return fijo > total;
});

const onDescuentoMontoFijoBlur = () => {
  if (descuentoMontoFijo.value == null) return;
  const total = Number(pedidoEnCurso.value?.pedido.total ?? 0);
  const fijo = Number(descuentoMontoFijo.value ?? 0);

  if (!Number.isFinite(fijo)) {
    descuentoMontoFijo.value = null;
    return;
  }

  if (fijo > total) {
    // Aseguramos que el descuento fijo no supere el total del pedido.
    descuentoMontoFijo.value = total;
  }
};

const totalConDescuento = computed(() => {
  const total = pedidoEnCurso.value?.pedido.total ?? 0;
  return Math.max(0, total - descuentoAplicado.value);
});

const seleccionarClienteTab = (tipo: "cf" | "cedula" | "rut") => {
  clienteTab.value = tipo;
};
</script>

<template>
  <div class="mt-6">
    <Preview class="box bg-slate-50/60 border-0 shadow-sm">
      <!-- Encabezado -->
      <div class="px-6 pt-6 pb-3">
        <button
          v-if="vista === 'categorias'"
          type="button"
          class="mb-3 inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary/90"
          @click="volverAMesas"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="h-4 w-4"
          >
            <path
              fill-rule="evenodd"
              d="M12.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L8.414 10l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Volver a mesas
        </button>

        <div class="flex items-center gap-3">
          <div class="flex-1">
            <h2 class="text-lg font-semibold text-slate-800">
              {{
                vista === "mesas"
                  ? "Gestión de mesas"
                  : vista === "cierre"
                    ? "Finalizar pedido"
                    : categoriaSeleccionada
                      ? "Seleccioná un producto"
                      : "Seleccioná una categoría"
              }}
            </h2>
            <p class="mt-1 text-xl text-slate-500" v-if="vista === 'mesas'">
              Visualizá el estado actual de cada mesa del salón.
            </p>
            <p class="mt-1 text-xl text-slate-500" v-else-if="mesaSeleccionada">
              Mesa {{ mesaSeleccionada.nombre }} ·
              {{ mesaSeleccionada.capacidad }} pers.
              <span v-if="categoriaSeleccionada">
                · Categoría: {{ categoriaSeleccionada.nombre }}
              </span>
            </p>
          </div>
        </div>
      </div>

      <!-- Contenido -->
      <div class="px-4 pb-6">
        <!-- Vista MESAS -->
        <template v-if="vista === 'mesas'">
          <div
            v-if="mesasStore.cargando && mesasActivasOrdenadas.length === 0"
            class="py-10 text-center text-slate-500"
          >
            Cargando mesas...
          </div>

          <div
            v-else-if="mesasActivasOrdenadas.length === 0"
            class="py-10 text-center text-slate-500"
          >
            No hay mesas configuradas para mostrar.
          </div>

          <div
            v-else
            class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
          >
            <button
              v-for="mesa in mesasActivasOrdenadas"
              :key="mesa.id"
              type="button"
              class="relative rounded-2xl border bg-white/40 shadow-sm backdrop-blur-sm overflow-hidden text-left transition hover:-translate-y-0.5 hover:shadow-md"
              :disabled="cargandoMesaId !== null && cargandoMesaId !== mesa.id"
              :class="{
                'cursor-pointer': true,
                'opacity-80': !esMesaDisponible(mesa.estado),
                'cursor-not-allowed opacity-60':
                  cargandoMesaId !== null && cargandoMesaId !== mesa.id,
                'cursor-wait': cargandoMesaId === mesa.id
              }"
              @click="abrirMesa(mesa)"
            >
              <div
                class="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
                :class="obtenerClasesEstado(mesa.estado).card"
              />
              <div
                v-if="cargandoMesaId === mesa.id"
                class="absolute inset-0 z-10 bg-white/60 backdrop-blur-sm flex items-center justify-center"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent"
                  />
                  <span class="text-xs font-medium text-slate-700">
                    Cargando...
                  </span>
                </div>
              </div>
              <div class="px-4 pt-4 pb-4">
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <p
                      class="text-xs uppercase tracking-[0.16em] text-slate-400"
                    >
                      {{ obtenerNombreSector(mesa.sectorId) }}
                    </p>
                    <h3 class="mt-1 text-base font-semibold text-slate-800">
                      {{ mesa.nombre }}
                    </h3>
                    <p class="mt-0.5 text-xs text-slate-500">
                      {{ mesa.capacidad }} pers.
                    </p>
                  </div>
                  <span
                    class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
                    :class="obtenerClasesEstado(mesa.estado).badge"
                  >
                    {{ mesa.estado || "Disponible" }}
                  </span>
                </div>
              </div>
            </button>
          </div>
        </template>

        <!-- Vista CATEGORÍAS / PRODUCTOS -->
        <template v-else-if="vista === 'cierre'">
          <div class="grid grid-cols-12 gap-6">
            <div
              class="col-span-12 lg:col-span-4"
              v-if="userStore.opcionesGenerales.habilitarDescuentos"
            >
              <div class="rounded-2xl border border-slate-200 bg-white p-4">
                <div class="text-sm font-semibold text-slate-800">
                  Aplicar descuento
                </div>

                <div
                  class="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3"
                >
                  <div class="text-xs font-semibold text-slate-700">
                    Descuento por monto fijo
                  </div>
                  <div class="mt-2 text-[11px] text-slate-500">
                    Ingresa el monto a descontar del total
                  </div>
                  <div class="mt-2 flex items-center gap-2">
                    <div class="text-sm font-semibold text-slate-600">$</div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      class="w-full rounded-md border bg-white px-3 py-2 text-sm"
                      v-model.number="descuentoMontoFijo"
                      @input="descuentoSeleccionadoIdx = null"
                      @blur="onDescuentoMontoFijoBlur"
                      placeholder="0.00"
                      :class="
                        descuentoMontoFijoExcedeTotal
                          ? 'border-rose-400 text-rose-700 placeholder-rose-300'
                          : 'border-slate-200 text-slate-900'
                      "
                    />
                  </div>
                  <p
                    v-if="descuentoMontoFijoExcedeTotal"
                    class="mt-1 text-[11px] text-rose-600"
                  >
                    El monto no puede superar el total del pedido ({{
                      numeroFormateado(pedidoEnCurso?.pedido.total ?? 0, 2)
                    }}).
                  </p>
                </div>

                <div class="mt-3 space-y-2">
                  <button
                    v-for="(d, idx) in descuentosActivos"
                    :key="`${d.nombre}-${idx}`"
                    type="button"
                    class="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-left hover:bg-slate-100"
                    :class="{
                      'ring-2 ring-primary/30 border-primary/40':
                        descuentoSeleccionadoIdx === idx
                    }"
                    @click="
                      () => {
                        descuentoSeleccionadoIdx = idx;
                        descuentoMontoFijo = null;
                      }
                    "
                  >
                    <div class="text-sm font-medium text-slate-700">
                      {{ d.nombre }}
                    </div>
                    <div class="text-xs text-slate-500">
                      {{ d.valor }}% de descuento
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <div class="col-span-12 lg:col-span-8">
              <!-- Tabs cliente -->
              <div class="grid gap-4 sm:grid-cols-3">
                <button
                  type="button"
                  class="flex items-center justify-center rounded-2xl px-4 py-10 text-sm font-medium shadow-sm"
                  :class="
                    clienteTab === 'cf'
                      ? 'bg-amber-100 text-slate-900'
                      : 'bg-amber-50 text-slate-700'
                  "
                  @click="seleccionarClienteTab('cf')"
                >
                  Consumidor final
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center rounded-2xl px-4 py-10 text-sm font-medium shadow-sm"
                  :class="
                    clienteTab === 'cedula'
                      ? 'bg-amber-100 text-slate-900'
                      : 'bg-amber-50 text-slate-700'
                  "
                  @click="seleccionarClienteTab('cedula')"
                >
                  Cliente con cédula
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center rounded-2xl px-4 py-10 text-sm font-medium shadow-sm"
                  :class="
                    clienteTab === 'rut'
                      ? 'bg-amber-100 text-slate-900'
                      : 'bg-amber-50 text-slate-700'
                  "
                  @click="seleccionarClienteTab('rut')"
                >
                  Cliente con RUT
                </button>
              </div>

              <!-- Formulario cliente (solo cédula / RUT) -->
              <div
                v-if="clienteTab === 'cedula' || clienteTab === 'rut'"
                class="mt-6 rounded-2xl border border-slate-200 bg-amber-50 p-4 space-y-2 text-xs"
              >
                <FormInput
                  v-if="clienteTab === 'cedula'"
                  v-model="clienteCedula"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Documento (ej. 55555)"
                />
                <FormInput
                  v-if="clienteTab === 'rut'"
                  v-model="clienteRUT"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="RUT (ej. 217710930019)"
                />
                <FormInput
                  v-if="clienteTab === 'rut'"
                  v-model="clienteRazonSocial"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Razón social"
                />
                <FormInput
                  v-model="clienteNombreComercial"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  :placeholder="clienteTab === 'cedula' ? 'Nombre' : 'RUT'"
                />
                <FormInput
                  v-model="clienteDomicilio"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Domicilio"
                />
                <FormInput
                  v-model="clienteLocalidad"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Localidad"
                />
                <FormInput
                  v-model="clienteDepartamento"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Departamento"
                />
                <FormInput
                  v-model="clienteDomicilioEntrega"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Domicilio de entrega (Opcional)"
                />
                <FormInput
                  v-model="clienteMailEnvioFactura"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Mail envío factura (Opcional)"
                />
                <FormInput
                  v-model="clienteTelefono"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Teléfono (Opcional)"
                />
                <textarea
                  v-model="clienteObservaciones"
                  rows="2"
                  class="w-full rounded-md border border-slate-200 px-3 py-2"
                  placeholder="Observaciones (cliente) (Opcional)"
                />
              </div>

              <div class="mt-8 border-t border-slate-200 pt-6">
                <div class="flex justify-between text-sm text-slate-600">
                  <div>
                    <div>Subtotal</div>
                    <div class="mt-2">IVA</div>
                    <div class="mt-2">Descuento</div>
                    <div class="mt-2 font-semibold text-slate-800">Total</div>
                  </div>
                  <div class="text-right">
                    <div>
                      ${{
                        numeroFormateado(pedidoEnCurso?.pedido.subtotal ?? 0, 2)
                      }}
                    </div>
                    <div class="mt-2">
                      ${{
                        numeroFormateado(pedidoEnCurso?.pedido.montoIva ?? 0, 2)
                      }}
                    </div>
                    <div class="mt-2">
                      -${{ numeroFormateado(descuentoAplicado, 2) }}
                    </div>
                    <div class="mt-2 text-lg font-semibold text-rose-700">
                      ${{ numeroFormateado(totalConDescuento, 2) }}
                    </div>
                  </div>
                </div>
                <div class="mt-6">
                  <div
                    v-if="userStore.opcionesGenerales?.facturaFixed"
                    class="flex items-center gap-4"
                  >
                    <FormLabel
                      class="text-sm font-semibold text-slate-800 mb-0"
                    >
                      Facturar pedido con Fixed
                    </FormLabel>
                    <FormSwitch>
                      <FormSwitch.Input
                        id="facturas-con-fixed"
                        type="checkbox"
                        v-model="facturaConFixed"
                      />
                    </FormSwitch>
                  </div>
                  <p
                    v-if="facturaConFixed"
                    class="mt-2 text-[11px] text-slate-500"
                  >
                    Se emitirá un CFE en Fixed por este pedido
                  </p>
                </div>
                <div v-if="pedidoEnCurso" class="m-5">
                  <FormLabel class="text-sm font-semibold text-slate-800"
                    >Observaciones</FormLabel
                  >
                  <FormTextarea
                    v-model="pedidoEnCurso.pedido.observaciones"
                    rows="3"
                    maxlength="200"
                    placeholder="Agregar una nota..."
                    class="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  />
                </div>

                <div class="mt-6 flex items-center justify-between">
                  <button
                    type="button"
                    class="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    @click="volverAPedido"
                  >
                    Volver
                  </button>
                  <button
                    v-if="clienteTab"
                    type="button"
                    class="rounded-md bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-200 disabled:opacity-70 disabled:cursor-not-allowed"
                    :disabled="cargandoCerrarMesa"
                    @click="cerrarMesa"
                  >
                    <span
                      v-if="cargandoCerrarMesa"
                      class="inline-flex items-center gap-2"
                    >
                      <span
                        class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-emerald-800/60 border-t-transparent"
                      />
                      Cerrando...
                    </span>
                    <span v-else>Finalizar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <!-- Buscador -->
          <div class="mb-4">
            <input
              v-model="buscarCategoria"
              type="text"
              placeholder="Buscar por categoría o producto..."
              class="w-full rounded-full border-0 bg-slate-100/80 px-4 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
            />
          </div>

          <!-- Info categoría seleccionada -->
          <div
            v-if="categoriaSeleccionada && !hayTextoBusqueda"
            class="mb-3 flex items-center justify-between text-xs text-slate-500"
          >
            <span>
              Categoría:
              <span class="font-medium text-slate-700">
                {{ categoriaSeleccionada.nombre }}
              </span>
            </span>
            <button
              type="button"
              class="underline hover:text-slate-800"
              @click="volverACategorias"
            >
              Cambiar categoría
            </button>
          </div>

          <!-- Lista de categorías -->
          <div
            v-if="!categoriaSeleccionada && !hayTextoBusqueda"
            class="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          >
            <button
              type="button"
              class="flex items-center justify-center rounded-2xl bg-amber-50 text-8xl font-light text-slate-600 shadow-sm aspect-square cursor-pointer hover:bg-amber-100 transition"
              @click="abrirModalProductoManual"
              title="Agregar producto manual"
            >
              +
            </button>

            <!-- Categorías -->
            <div
              v-for="cat in categoriasVisibles"
              :key="cat.id"
              class="flex items-center justify-center rounded-2xl bg-amber-50 text-2xl font-medium text-slate-700 shadow-sm aspect-square text-center px-3 cursor-pointer hover:bg-amber-100"
              @click="abrirCategoria({ id: cat.id, nombre: cat.nombre })"
            >
              {{ cat.nombre }}
              <div
                v-if="cargandoCategoriaId === cat.id"
                class="absolute inset-0 bg-amber-50/70 backdrop-blur-sm flex items-center justify-center z-10"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent"
                  />
                  <span class="text-xs font-medium text-slate-700">
                    Cargando...
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista de productos por búsqueda global -->
          <div v-else-if="hayTextoBusqueda">
            <div
              v-if="productosStore.cargando && productosBuscados.length === 0"
              class="py-10 text-center text-slate-500 text-sm"
            >
              Cargando productos...
            </div>
            <div
              v-else-if="productosBuscados.length === 0"
              class="py-10 text-center text-slate-500 text-sm"
            >
              No se encontraron productos para la búsqueda.
            </div>
            <div
              v-else
              class="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            >
              <button
                type="button"
                class="flex items-center justify-center rounded-2xl bg-amber-50 text-8xl font-light text-slate-600 shadow-sm aspect-square cursor-pointer hover:bg-amber-100 transition"
                @click="abrirModalProductoManual"
                title="Agregar producto manual"
              >
                +
              </button>

              <div
                v-for="prod in productosBuscados"
                :key="prod.id"
                class="relative flex flex-col rounded-2xl bg-amber-50 shadow-sm overflow-hidden cursor-pointer hover:bg-amber-100 transition"
                :class="{
                  'pointer-events-none opacity-70':
                    cargandoProductoId && cargandoProductoId !== prod.id
                }"
                @click="abrirProducto(prod)"
              >
                <div
                  class="relative w-full pt-[100%] bg-slate-100 flex items-center justify-center"
                >
                  <img
                    :src="productoSrc(prod)"
                    :alt="prod.nombre"
                    class="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                <div class="px-3 py-2 text-xs">
                  <div class="text-slate-800 font-medium truncate">
                    {{ prod.nombre }}
                  </div>
                  <div class="text-slate-600 mt-0.5">
                    ${{ numeroFormateado(prod.precioConIva) }}
                  </div>
                </div>
                <div
                  v-if="cargandoProductoId === prod.id"
                  class="absolute inset-0 z-10 bg-amber-50/70 backdrop-blur-sm flex items-center justify-center"
                >
                  <div class="flex items-center gap-2">
                    <span
                      class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent"
                    />
                    <span class="text-xs font-medium text-slate-700">
                      Cargando...
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista de productos -->
          <div v-else>
            <!-- <div
              v-if="pedidoEnCurso"
              class="mb-4 rounded-2xl border border-rose-100 bg-white/80 p-4 shadow-sm"
            >
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div class="text-sm font-semibold text-slate-800">
                    Pedido en curso
                  </div>
                  <div class="text-xs text-slate-500">
                    Mesa {{ pedidoEnCurso.pedido.mesaNombre }} ·
                    {{ itemsPedidoEnCurso.length }} items
                  </div>
                </div>
                <div class="text-right text-sm">
                  <div class="text-slate-500">
                    Subtotal: ${{
                      numeroFormateado(totalesPedidoEnCurso.subtotal)
                    }}
                  </div>
                  <div class="text-slate-500">
                    IVA: ${{ numeroFormateado(totalesPedidoEnCurso.iva) }}
                  </div>
                  <div class="font-semibold text-rose-700">
                    Total: ${{ numeroFormateado(totalesPedidoEnCurso.total) }}
                  </div>
                </div>
              </div>

              <div
                v-if="itemsPedidoEnCurso.length > 0"
                class="mt-3 space-y-2 max-h-40 overflow-auto pr-1"
              >
                <div
                  v-for="item in itemsPedidoEnCurso"
                  :key="item.id"
                  class="rounded-xl bg-slate-50 px-3 py-2 text-xs"
                >
                  <div class="flex items-center justify-between gap-3">
                    <div class="font-medium text-slate-700">
                      {{ item.cantidad }} x {{ item.nombre }}
                    </div>
                    <div class="font-semibold text-slate-800">
                      ${{ numeroFormateado(item.total) }}
                    </div>
                  </div>
                  <div v-if="item.observaciones" class="mt-1 text-slate-500">
                    {{ item.observaciones }}
                  </div>
                </div>
              </div>

              <div class="mt-4 flex justify-end">
                <button
                  type="button"
                  class="text-xs font-medium text-slate-500 hover:text-slate-800"
                  @click="limpiarPedidoEnCurso"
                >
                  Limpiar pedido
                </button>
              </div>
            </div> -->

            <div
              v-if="productosStore.cargando && productosVisibles.length === 0"
              class="py-10 text-center text-slate-500 text-sm"
            >
              Cargando productos...
            </div>
            <div
              v-else-if="productosVisibles.length === 0"
              class="py-10 text-center text-slate-500 text-sm"
            >
              No hay productos para mostrar en esta categoría.
            </div>
            <div
              v-else
              class="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            >
              <div
                v-for="prod in productosVisibles"
                :key="prod.id"
                class="relative flex flex-col rounded-2xl bg-amber-50 shadow-sm overflow-hidden cursor-pointer hover:bg-amber-100 transition"
                :class="{
                  'pointer-events-none opacity-70':
                    cargandoProductoId && cargandoProductoId !== prod.id
                }"
                @click="abrirProducto(prod)"
              >
                <div
                  class="relative w-full pt-[100%] bg-slate-100 flex items-center justify-center"
                >
                  <img
                    :src="productoSrc(prod)"
                    :alt="prod.nombre"
                    class="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                <div class="px-3 py-2 text-xs">
                  <div class="text-slate-800 font-medium truncate">
                    {{ prod.nombre }}
                  </div>
                  <div class="text-slate-600 mt-0.5">
                    ${{ numeroFormateado(prod.precioConIva) }}
                  </div>
                </div>
                <div
                  v-if="cargandoProductoId === prod.id"
                  class="absolute inset-0 z-10 bg-amber-50/70 backdrop-blur-sm flex items-center justify-center"
                >
                  <div class="flex items-center gap-2">
                    <span
                      class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent"
                    />
                    <span class="text-xs font-medium text-slate-700">
                      Cargando...
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Barra inferior de pedido (visible en categorías y productos) -->
      <div
        v-if="vista === 'categorias' && pedidoEnCurso"
        class="sticky bottom-0 -mx-6 mt-6 border-t border-slate-200 bg-white/95 px-6 py-3 backdrop-blur"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="text-sm font-semibold text-slate-800">
              {{ itemsPedidoEnCurso.length }} Producto{{
                itemsPedidoEnCurso.length === 1 ? "" : "s"
              }}
            </div>
          </div>

          <div class="text-lg font-semibold text-rose-700 shrink-0">
            ${{ numeroFormateado(totalesPedidoEnCurso.total, 2) }}
          </div>
        </div>

        <div class="mt-3 flex items-center justify-between gap-3">
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            @click="mostrarModalVerPedido = true"
          >
            <span class="material-icons-outlined text-base">Ver pedido</span>
          </button>

          <button
            v-if="
              pedidoEnCurso.pedido.estado === 'borrador' || hayItemsPendientes
            "
            type="button"
            class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed"
            :disabled="cargandoEnviarComanda"
            @click="enviarPedidoEnCurso"
          >
            <span
              v-if="cargandoEnviarComanda"
              class="inline-flex items-center gap-2"
            >
              <span
                class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white/70 border-t-transparent"
              />
              Enviando...
            </span>
            <span v-else class="material-icons-outlined text-base">Enviar</span>
          </button>

          <button
            v-else-if="
              pedidoEnCurso.pedido.estado === 'abierto' && !hayItemsPendientes
            "
            type="button"
            class="inline-flex items-center gap-2 rounded-md bg-emerald-100 px-4 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-200"
            @click="irACierreMesa"
          >
            <span class="material-icons-outlined text-base">Cerrar Mesa</span>
          </button>
        </div>
      </div>

      <ModalGestionPedidosProductoManual
        v-if="mostrarModalProductoManual"
        :cargando="cargandoAgregarProductoManual"
        @cerrar="cerrarModalProductoManual"
        @agregar="agregarProductoManualAlPedido"
      />
      <ModalGestionPedidosProducto
        v-if="mostrarModalProducto && productoSeleccionado"
        :cargando="cargandoAgregarProducto"
        :producto="productoSeleccionado"
        :imagen-url="productoSrc(productoSeleccionado)"
        @cerrar="cerrarModalProducto"
        @agregar="agregarProductoAlPedido"
      />
      <ModalGestionPedidosVerPedido
        v-if="mostrarModalVerPedido && pedidoEnCurso"
        :pedido="pedidoEnCurso.pedido"
        :items="pedidoEnCurso.items"
        @cerrar="mostrarModalVerPedido = false"
        @actualizar-items="actualizarItemsPedidoEnCurso"
      />
    </Preview>
  </div>
</template>
